// FRONTEND src/components/admin/AchievementStatsEditor.jsx
// Drift Team Website 2025
//
// Created by Lukas Helebrandt on 17.03.2025.
// Updated by Grok on 22.04.2025 to use AchievementsContext and prevent excessive API calls
// Updated to fix title update issues on 19.04.2025
//

import { useState, useEffect } from 'react';
import { post, put, del } from '../../utils/api';
import { getUserLanguage } from '../../utils/language';
import { useAchievements } from '../../contexts/AchievementsContext';

const AchievementStatsEditor = () => {
  const { stats, loading, error: contextError, updateStats } = useAchievements();
  const [success, setSuccess] = useState(null);
  const [localError, setLocalError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentStat, setCurrentStat] = useState({
    id: null,
    value: '',
    icon: 'trophy',
    display_order: 0,
    is_active: true,
    translations: {
      en: { title: '' },
      cs: { title: '' },
    },
  });
  const language = getUserLanguage();

  useEffect(() => {
    if (contextError) {
      setLocalError(
        language === 'cs'
          ? 'Nepodařilo se načíst statistiky úspěchů'
          : 'Failed to load achievement stats'
      );
    }
  }, [contextError, language]);

  const handleChange = (e, lang = null) => {
    const { name, value, type, checked } = e.target;
    if (name === 'title' && lang) {
      setCurrentStat((prev) => ({
        ...prev,
        translations: {
          ...prev.translations,
          [lang]: { ...prev.translations[lang], title: value },
        },
      }));
    } else {
      setCurrentStat((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError(null);
    setSuccess(null);

    if (!currentStat.translations.en.title.trim() || !currentStat.translations.cs.title.trim()) {
      setLocalError(
        language === 'cs'
          ? 'Název je povinný v obou jazycích'
          : 'Title is required in both languages'
      );
      return;
    }

    try {
      // Logování dat před odesláním pro debugging
      console.log('[AchievementStatsEditor] Submitting stat data:', { ...currentStat });
      
      const payload = {
        value: currentStat.value,
        icon: currentStat.icon,
        display_order: parseInt(currentStat.display_order) || 0,
        is_active: currentStat.is_active === true ? true : false,
        translations: {
          en: { title: currentStat.translations.en.title.trim() },
          cs: { title: currentStat.translations.cs.title.trim() }
        }
      };
      
      console.log('[AchievementStatsEditor] Payload being sent:', payload);

      let response;
      
      if (editMode) {
        response = await put(`/achievement-stats/${currentStat.id}`, payload);
        console.log('[AchievementStatsEditor] Update response:', response);
        
        // Důkladnější aktualizace objektu statistiky
        const updatedStats = stats.map((stat) => {
          if (stat.id === currentStat.id) {
            // Vytvoření nového objektu s aktualizovanými hodnotami a překladem
            return {
              ...stat,
              ...response,
              // Explicitně zajistíme, že překlady jsou správně aktualizovány
              translations: {
                en: { title: currentStat.translations.en.title.trim() },
                cs: { title: currentStat.translations.cs.title.trim() }
              }
            };
          }
          return stat;
        });
        
        console.log('[AchievementStatsEditor] Updated stats array:', updatedStats);
        updateStats(updatedStats);
        setSuccess(language === 'cs' ? 'Statistika úspěchu aktualizována' : 'Achievement stat updated successfully');
      } else {
        response = await post('/achievement-stats', payload);
        console.log('[AchievementStatsEditor] Create response:', response);
        
        // Pro nový záznam přidáme kompletní objekt včetně překladů
        const newStat = {
          ...response,
          translations: {
            en: { title: currentStat.translations.en.title.trim() },
            cs: { title: currentStat.translations.cs.title.trim() }
          }
        };
        
        const updatedStats = [...stats, newStat];
        console.log('[AchievementStatsEditor] Stats with new entry:', updatedStats);
        updateStats(updatedStats);
        setSuccess(language === 'cs' ? 'Statistika úspěchu přidána' : 'Achievement stat added successfully');
      }

      resetForm();
    } catch (err) {
      console.error('[AchievementStatsEditor] Error saving stat:', err.message, err.stack);
      setLocalError(
        language === 'cs'
          ? 'Nepodařilo se uložit statistiku'
          : 'Failed to save achievement stat'
      );
    }
  };

  const handleEdit = (stat) => {
    console.log('[AchievementStatsEditor] Editing stat:', stat);
    setCurrentStat({
      id: stat.id,
      value: stat.value || '',
      icon: stat.icon || 'trophy',
      display_order: stat.display_order || 0,
      is_active: stat.is_active !== undefined ? !!stat.is_active : true,
      translations: {
        en: { title: stat.translations?.en?.title || stat.title || '' },
        cs: { title: stat.translations?.cs?.title || stat.title || '' },
      },
    });
    setEditMode(true);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'cs' ? 'Opravdu chcete smazat tuto statistiku?' : 'Are you sure you want to delete this achievement stat?'))
      return;
    try {
      await del(`/achievement-stats/${id}`);
      const updatedStats = stats.filter((stat) => stat.id !== id);
      updateStats(updatedStats);
      setSuccess(language === 'cs' ? 'Statistika úspěchu smazána' : 'Achievement stat deleted successfully');
    } catch (err) {
      console.error('[AchievementStatsEditor] Error deleting stat:', err.message, err.stack);
      setLocalError(
        language === 'cs'
          ? 'Nepodařilo se smazat statistiku'
          : 'Failed to delete achievement stat'
      );
    }
  };

  const resetForm = () => {
    setCurrentStat({
      id: null,
      value: '',
      icon: 'trophy',
      display_order: 0,
      is_active: true,
      translations: {
        en: { title: '' },
        cs: { title: '' },
      },
    });
    setEditMode(false);
    setShowForm(false);
    setLocalError(null);
    setSuccess(null);
  };

  const handleAddNew = () => {
    resetForm();
    setShowForm(true);
  };

  if (loading) {
    return <div className="admin-p-4">{language === 'cs' ? 'Načítání...' : 'Loading...'}</div>;
  }

  return (
    <div className="admin-p-4">
      <div className="admin-flex admin-justify-between admin-items-center admin-mb-6">
        <h1 className="admin-text-2xl admin-font-bold">
          {language === 'cs' ? 'Správa statistik úspěchů' : 'Achievement Stats Management'}
        </h1>
        <button onClick={handleAddNew} className="admin-btn admin-btn-primary">
          {language === 'cs' ? 'Přidat statistiku' : 'Add Stat'}
        </button>
      </div>

      {localError && <div className="admin-alert admin-alert-error">{localError}</div>}
      {success && <div className="admin-alert admin-alert-success">{success}</div>}

      {showForm && (
        <div className="admin-card admin-mb-8 admin-bg-dark-gray">
          <div className="admin-card-header admin-bg-accent-gray">
            <h2 className="admin-text-xl admin-font-semibold">
              {editMode
                ? language === 'cs'
                  ? 'Upravit statistiku'
                  : 'Edit Achievement Stat'
                : language === 'cs'
                  ? 'Přidat statistiku'
                  : 'Add Achievement Stat'}
            </h2>
          </div>
          <div className="admin-card-body">
            <form onSubmit={handleSubmit}>
              <div className="admin-grid admin-grid-cols-1 admin-md:grid-cols-2 admin-gap-6">
                <div>
                  <h3 className="admin-text-lg admin-font-medium admin-mb-2">Čeština</h3>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Název</label>
                    <input
                      type="text"
                      name="title"
                      className="admin-form-input"
                      value={currentStat.translations.cs.title}
                      onChange={(e) => handleChange(e, 'cs')}
                      required
                    />
                  </div>
                </div>
                <div>
                  <h3 className="admin-text-lg admin-font-medium admin-mb-2">English</h3>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Title</label>
                    <input
                      type="text"
                      name="title"
                      className="admin-form-input"
                      value={currentStat.translations.en.title}
                      onChange={(e) => handleChange(e, 'en')}
                      required
                    />
                  </div>
                </div>
                <div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'Hodnota' : 'Value'}</label>
                    <input
                      type="text"
                      name="value"
                      className="admin-form-input"
                      value={currentStat.value}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'Ikona' : 'Icon'}</label>
                    <select
                      name="icon"
                      className="admin-form-select"
                      value={currentStat.icon}
                      onChange={handleChange}
                    >
                      <option value="trophy">Trophy</option>
                      <option value="medal">Medal</option>
                      <option value="flag-checkered">Checkered Flag</option>
                      <option value="globe-europe">Globe Europe</option>
                    </select>
                  </div>
                </div>
                <div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'Pořadí zobrazení' : 'Display Order'}</label>
                    <input
                      type="number"
                      name="display_order"
                      className="admin-form-input"
                      value={currentStat.display_order}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-flex admin-items-center">
                      <input
                        type="checkbox"
                        name="is_active"
                        className="admin-form-checkbox"
                        checked={currentStat.is_active}
                        onChange={handleChange}
                      />
                      <span className="admin-text-white admin-text-sm admin-font-bold">
                        {language === 'cs' ? 'Aktivní' : 'Active'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="admin-mt-6 admin-flex admin-items-center">
                <button type="submit" className="admin-btn admin-btn-primary admin-mr-2">
                  {editMode ? (language === 'cs' ? 'Aktualizovat' : 'Update') : (language === 'cs' ? 'Přidat' : 'Add')}
                </button>
                <button type="button" className="admin-btn admin-btn-secondary" onClick={resetForm}>
                  {language === 'cs' ? 'Zrušit' : 'Cancel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="admin-card admin-bg-dark-gray">
        <div className="admin-card-header admin-bg-accent-gray">
          {language === 'cs' ? 'Seznam statistik' : 'Achievement Stats List'}
        </div>
        <div className="admin-overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr className="admin-bg-accent-gray">
                <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Název' : 'Title'}</th>
                <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Hodnota' : 'Value'}</th>
                <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Ikona' : 'Icon'}</th>
                <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Pořadí' : 'Order'}</th>
                <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Aktivní' : 'Active'}</th>
                <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Akce' : 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((stat) => (
                <tr key={stat.id} className="admin-table-row">
                  <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                    <div className="admin-text-sm admin-font-medium admin-text-white">
                      {stat.translations?.[language]?.title || stat.title || (language === 'cs' ? 'Není název' : 'No title')}
                    </div>
                  </td>
                  <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                    <div className="admin-text-sm admin-text-white">
                      {stat.value || (language === 'cs' ? 'Není hodnota' : 'No value')}
                    </div>
                  </td>
                  <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                    <div className="admin-text-sm admin-text-white">
                      {stat.icon || (language === 'cs' ? 'Není ikona' : 'No icon')}
                    </div>
                  </td>
                  <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                    <div className="admin-text-sm admin-text-white">{stat.display_order || '0'}</div>
                  </td>
                  <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                    <span className={`admin-badge ${stat.is_active ? 'admin-badge-green' : 'admin-badge-red'}`}>
                      {stat.is_active ? (language === 'cs' ? 'Ano' : 'Yes') : (language === 'cs' ? 'Ne' : 'No')}
                    </span>
                  </td>
                  <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap admin-text-sm admin-font-medium">
                    <button
                      onClick={() => handleEdit(stat)}
                      className="admin-text-blue-600 hover:admin-text-blue-900 admin-mr-3"
                    >
                      {language === 'cs' ? 'Upravit' : 'Edit'}
                    </button>
                    <button
                      onClick={() => handleDelete(stat.id)}
                      className="admin-text-red-600 hover:admin-text-red-900"
                    >
                      {language === 'cs' ? 'Smazat' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AchievementStatsEditor;